import { useState, useMemo, useEffect } from "react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import {
  Users,
  Plus,
  Download,
  Search,
  Shield,
  ShieldCheck,
  Globe,
  User as UserIcon,
  Eye,
  Pencil,
  Ban,
  CheckCircle2,
  Clock,
  Mail,
  Send,
  Info,
  X,
  Trash2,
  Loader2,
  Phone,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { createClient } from "@supabase/supabase-js";
import {
  getSupabaseClient,
  getSupabaseUrl,
} from "../providers/supabase/supabase";
import type { Sale, UserStatus, PermissionLevel } from "../types";
import type { CrmDataProvider } from "../providers/types";
import { useCRM } from "../context/CRMContext";
import { isSuperAdminEmail } from "../admin/superadminConfig";
import {
  getConnectedGoogleUser,
  getConnectedGoogleUserBySalesId,
  connectGoogleOAuth,
  disconnectGoogleUser,
  sendGmailInvite,
  type GoogleUser,
} from "../providers/google/googleIntegrationService";

export function SalesList() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const { currentCrm } = useCRM();
  const notify = useNotify();

  const [users, setUsers] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(() =>
    identity?.id != null
      ? getConnectedGoogleUser(identity.id)
      : getConnectedGoogleUser(),
  );

  useEffect(() => {
    if (identity?.id != null) {
      setGoogleUser(getConnectedGoogleUser(identity.id));
    }
  }, [identity?.id]);

  // Status Filter: "active" | "invited" | "disabled" | "all"
  const [statusFilter, setStatusFilter] = useState<UserStatus | "all">(
    "active",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  // Modals state
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isPermissionsGuideOpen, setIsPermissionsGuideOpen] = useState(false);
  const [activeUser, setActiveUser] = useState<Sale | null>(null);

  // Delete states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Sale | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Form states
  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    status: UserStatus;
    deal_permission: PermissionLevel;
    global_permission: PermissionLevel;
    send_invite: boolean;
    dotus_instance_id?: string;
  }>({
    first_name: "",
    last_name: "",
    email: "",
    status: "active",
    deal_permission: "user",
    global_permission: "user",
    send_invite: true,
    dotus_instance_id: "",
  });

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await dataProvider.getList<Sale>("sales", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "first_name", order: "ASC" },
        filter: {},
      });
      // Normalize statuses and permissions if missing, excluindo o Super Admin
      const nonSuperadmin = (res.data || []).filter(
        (u) => !isSuperAdminEmail(u.email),
      );
      // Deduplicação defensiva por id e por e-mail para evitar qualquer linha triplicada
      const seenIds = new Set<any>();
      const seenEmails = new Set<string>();
      const uniqueUsers = nonSuperadmin.filter((u) => {
        if (!u) return false;
        const email = u.email?.trim().toLowerCase();
        if (seenIds.has(u.id) || (email && seenEmails.has(email))) {
          return false;
        }
        if (u.id) seenIds.add(u.id);
        if (email) seenEmails.add(email);
        return true;
      });

      const normalized: Sale[] = uniqueUsers.map((u) => {
        let status: UserStatus =
          u.status || (u.disabled ? "disabled" : "active");
        if (u.disabled) status = "disabled";
        return {
          ...u,
          status,
          deal_permission:
            u.deal_permission || (u.administrator ? "admin" : "user"),
          global_permission:
            u.global_permission || (u.administrator ? "admin" : "user"),
          last_login:
            u.last_login !== undefined
              ? u.last_login
              : status === "invited"
                ? null
                : "Hoje às 09:14",
          account_configured:
            u.account_configured !== undefined
              ? u.account_configured
              : status !== "invited",
        };
      });
      setUsers(normalized);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Counters
  const counts = useMemo(() => {
    const active = users.filter(
      (u) => u.status === "active" && !u.disabled,
    ).length;
    const invited = users.filter(
      (u) => u.status === "invited" && !u.disabled,
    ).length;
    const disabled = users.filter(
      (u) => u.status === "disabled" || u.disabled,
    ).length;
    return {
      active,
      invited,
      disabled,
      all: users.length,
    };
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      // Status filter
      if (statusFilter !== "all") {
        if (statusFilter === "disabled") {
          if (!u.disabled && u.status !== "disabled") return false;
        } else if (statusFilter === "active") {
          if (u.disabled || u.status !== "active") return false;
        } else if (statusFilter === "invited") {
          if (u.disabled || u.status !== "invited") return false;
        }
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
        const email = u.email?.toLowerCase() || "";
        if (!fullName.includes(q) && !email.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [users, statusFilter, searchQuery]);

  // Checkbox selection
  const allFilteredSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.includes(u.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredUsers.map((u) => u.id));
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      const newIds = new Set([
        ...selectedIds,
        ...filteredUsers.map((u) => u.id),
      ]);
      setSelectedIds(Array.from(newIds));
    }
  };

  const toggleSelect = (id: string | number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Actions
  const handleOpenAdd = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      status: "invited",
      deal_permission: "user",
      global_permission: "user",
      send_invite: true,
      dotus_instance_id: "",
    });
    setIsAddUserOpen(true);
  };

  const handleSaveAdd = async () => {
    if (isSavingAdd) return;
    if (!formData.first_name.trim() || !formData.email.trim()) {
      notify("Preencha o nome e e-mail do usuário.", { type: "warning" });
      return;
    }

    try {
      setIsSavingAdd(true);
      const tempPassword = `Dotus@${Math.random().toString(36).slice(2, 8)}!`;
      const newRecord = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim() || "",
        email: formData.email.trim(),
        password: tempPassword,
        administrator:
          formData.deal_permission === "admin" ||
          formData.global_permission === "admin",
        disabled: formData.status === "disabled",
        status: formData.status,
        deal_permission: formData.deal_permission,
        global_permission: formData.global_permission,
        account_configured: formData.status !== "invited",
        dotus_instance_id:
          formData.dotus_instance_id?.trim() ||
          formData.first_name.trim() ||
          null,
        last_login:
          formData.status === "invited" ? null : "Recentemente adicionado",
      };

      const res = await dataProvider.salesCreate(newRecord as any);
      setUsers((prev) => {
        const filtered = prev.filter(
          (u) =>
            u.id !== res?.id &&
            u.email.toLowerCase() !== formData.email.trim().toLowerCase(),
        );
        return [
          {
            ...res,
            ...newRecord,
            id: res?.id || Date.now(),
          },
          ...filtered,
        ];
      });

      if (formData.send_invite) {
        const inviteRes = await sendGmailInvite({
          to: formData.email.trim(),
          recipientName: formData.first_name.trim(),
          roleDeals: formData.deal_permission,
          roleGlobal: formData.global_permission,
          ownerName: googleUser?.name || identity?.fullName,
          ownerEmail: googleUser?.email,
          tempPassword: (res as any)?.tempPassword || tempPassword,
        });

        notify(inviteRes.message, {
          type: inviteRes.success ? "success" : "warning",
        });
      } else {
        notify("Usuário cadastrado com sucesso!", { type: "success" });
      }
      setIsAddUserOpen(false);
      // Sincroniza lista oficial do backend
      fetchUsers();
    } catch (err: any) {
      console.error(err);
      notify("Erro ao criar usuário: " + (err?.message || "Tente novamente"), {
        type: "error",
      });
    } finally {
      setIsSavingAdd(false);
    }
  };

  const handleOpenEdit = (user: Sale) => {
    setActiveUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name || "",
      email: user.email,
      status: (user.status ||
        (user.disabled ? "disabled" : "active")) as UserStatus,
      deal_permission:
        user.deal_permission || (user.administrator ? "admin" : "user"),
      global_permission:
        user.global_permission || (user.administrator ? "admin" : "user"),
      send_invite: false,
      dotus_instance_id: user.dotus_instance_id || "",
    });
    setIsEditUserOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!activeUser || isSavingEdit) return;
    try {
      setIsSavingEdit(true);
      const updatedFields = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        status: formData.status,
        disabled: formData.status === "disabled",
        administrator:
          formData.deal_permission === "admin" ||
          formData.global_permission === "admin",
        deal_permission: formData.deal_permission,
        global_permission: formData.global_permission,
        account_configured: formData.status !== "invited",
        dotus_instance_id: formData.dotus_instance_id?.trim() || null,
      };

      await dataProvider.salesUpdate(activeUser.id, updatedFields as any);

      setUsers((prev) =>
        prev.map((u) =>
          u.id === activeUser.id ? { ...u, ...updatedFields } : u,
        ),
      );

      notify("Usuário atualizado com sucesso!", { type: "success" });
      setIsEditUserOpen(false);
    } catch (err: any) {
      console.error(err);
      notify(
        "Erro ao atualizar usuário: " + (err?.message || "Tente novamente"),
        { type: "error" },
      );
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleStatus = async (user: Sale) => {
    const isCurrentlyDisabled = user.status === "disabled" || user.disabled;
    const newStatus: UserStatus = isCurrentlyDisabled ? "active" : "disabled";
    const newDisabled = !isCurrentlyDisabled;

    try {
      await dataProvider.salesUpdate(user.id, {
        disabled: newDisabled,
        status: newStatus,
      } as any);

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, disabled: newDisabled, status: newStatus }
            : u,
        ),
      );

      notify(
        newDisabled
          ? `Usuário ${user.first_name} desativado.`
          : `Usuário ${user.first_name} reativado!`,
        { type: "info" },
      );
    } catch {
      notify("Não foi possível alterar o status do usuário.", {
        type: "error",
      });
    }
  };

  const [resendingId, setResendingId] = useState<string | number | null>(null);

  const handleResendInvite = async (user: Sale) => {
    if (!user.email) {
      notify("Usuário não possui e-mail cadastrado.", { type: "warning" });
      return;
    }

    setResendingId(user.id);
    try {
      const tempPassword = `Dotus@${Math.random().toString(36).slice(2, 8)}!`;

      // 1. Cadastra/atualiza no Supabase Auth usando cliente isolado sem persistência de sessão
      const supabaseUrl = getSupabaseUrl();
      const supabaseKey = "dotus_crm_client_public";

      if (supabaseUrl && supabaseKey) {
        try {
          const isolatedClient = createClient(supabaseUrl, supabaseKey, {
            auth: { persistSession: false, autoRefreshToken: false },
          });
          await isolatedClient.auth.signUp({
            email: user.email.trim(),
            password: tempPassword,
            options: {
              data: {
                first_name: user.first_name,
                last_name: user.last_name || "",
              },
            },
          });
        } catch (authErr) {
          console.warn("Aviso ao sincronizar senha com Auth:", authErr);
        }
      }

      // Garante a gravação da senha provisória e confirmação via RPC no banco
      try {
        const client = getSupabaseClient();
        await client.rpc("set_team_user_password", {
          user_email: user.email.trim(),
          new_password: tempPassword,
        });
      } catch (rpcErr) {
        console.warn("RPC set_team_user_password:", rpcErr);
      }

      // 2. Atualiza a senha provisória e garante status no CRM
      try {
        await dataProvider.salesUpdate(user.id, {
          password: tempPassword,
          status: user.status || "invited",
        } as any);
      } catch (dbErr) {
        console.warn("Aviso ao atualizar registro em sales:", dbErr);
      }

      // 3. Envia o e-mail de convite com o link correto e senha de ativação
      const inviteRes = await sendGmailInvite({
        to: user.email.trim(),
        recipientName: user.first_name.trim(),
        roleDeals: user.deal_permission || "user",
        roleGlobal: user.global_permission || "user",
        ownerName: googleUser?.name || identity?.fullName,
        ownerEmail: googleUser?.email,
        tempPassword,
      });

      if (inviteRes.success) {
        notify(`Convite com link de ativação reenviado para ${user.email}!`, {
          type: "success",
        });
      } else {
        notify(inviteRes.message, { type: "warning" });
      }
    } catch (err: any) {
      console.error("Erro ao reenviar convite:", err);
      notify(
        "Erro ao reenviar convite: " + (err?.message || "Tente novamente"),
        {
          type: "error",
        },
      );
    } finally {
      setResendingId(null);
    }
  };

  const handleBulkStatusChange = async (targetStatus: UserStatus) => {
    if (!selectedIds.length) return;
    const isDisabling = targetStatus === "disabled";

    try {
      for (const id of selectedIds) {
        await dataProvider.salesUpdate(id, {
          disabled: isDisabling,
          status: targetStatus,
        } as any);
      }

      setUsers((prev) =>
        prev.map((u) =>
          selectedIds.includes(u.id)
            ? { ...u, disabled: isDisabling, status: targetStatus }
            : u,
        ),
      );

      notify(
        `${selectedIds.length} usuário(s) alterados para ${
          targetStatus === "active"
            ? "Ativo"
            : targetStatus === "invited"
              ? "Convidado"
              : "Desativado"
        }.`,
        { type: "success" },
      );
      setSelectedIds([]);
    } catch {
      notify("Erro ao atualizar usuários em lote.", { type: "error" });
    }
  };

  const handleOpenOverview = (user: Sale) => {
    setActiveUser(user);
    setIsOverviewOpen(true);
  };

  // CSV Export
  const handleExportCSV = (userList: Sale[] = filteredUsers) => {
    if (!userList.length) {
      notify("Nenhum usuário para exportar.", { type: "warning" });
      return;
    }

    const headers = [
      "Nome",
      "Sobrenome",
      "E-mail",
      "Status",
      "Último Login",
      "Permissão Deals",
      "Permissão Global",
    ];

    const rows = userList.map((u) => [
      `"${u.first_name}"`,
      `"${u.last_name || ""}"`,
      `"${u.email}"`,
      `"${u.status === "active" ? "Ativo" : u.status === "invited" ? "Convidado" : "Desativado"}"`,
      `"${u.last_login || "Nunca"}"`,
      `"${u.deal_permission === "admin" ? "Administrador dos negócios" : "Usuário comum de negócios"}"`,
      `"${u.global_permission === "admin" ? "Administrador global" : "Usuário comum global"}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8,\uFEFF" +
      [headers.join(";"), ...rows.map((e) => e.join(";"))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `usuarios_crm_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notify(`${userList.length} usuário(s) exportado(s) com sucesso!`, {
      type: "success",
    });
  };

  const isCurrentUser = (user: Sale) => {
    if (!identity) return false;
    return (
      user.id === identity.id ||
      user.email === (identity as any).email ||
      `${user.first_name} ${user.last_name}`.trim().toLowerCase() ===
        identity.fullName?.trim().toLowerCase()
    );
  };

  const isOwnerUser = (user: Sale) => {
    if (!currentCrm) return false;
    return (
      (currentCrm as any).owner_id != null &&
      ((currentCrm as any).owner_id === user.user_id ||
        (currentCrm as any).owner_id === (user as any).id)
    );
  };

  const handleDeleteUserClick = (user: Sale) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      setIsDeleting(true);
      await (dataProvider as any).salesDelete(userToDelete.id);
      notify("Membro removido da equipe com sucesso!", { type: "info" });
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setSelectedIds((prev) => prev.filter((id) => id !== userToDelete.id));
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error("Erro ao excluir usuário:", err);
      notify("Erro ao excluir usuário: " + (err.message || "Tente novamente"), {
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsBulkDeleting(true);
      for (const id of selectedIds) {
        await (dataProvider as any).salesDelete(id);
      }
      notify(
        `${selectedIds.length} membro(s) removido(s) da equipe com sucesso!`,
        { type: "info" },
      );
      setUsers((prev) => prev.filter((u) => !selectedIds.includes(u.id)));
      setSelectedIds([]);
      setIsBulkDeleteDialogOpen(false);
    } catch (err: any) {
      console.error("Erro ao excluir usuários em massa:", err);
      notify("Erro ao excluir usuários selecionados.", { type: "error" });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex-1 space-y-5 p-4 md:p-8 pt-6 max-w-7xl mx-auto">
        {/* Top Header / Page Title */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/40 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
                <Users className="w-7 h-7 text-primary" />
                Time
              </h1>
              <Badge variant="secondary" className="text-xs font-medium">
                Usuários e Acesso
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Gerencie os membros da sua equipe, permissões de negócios (Deals)
              e recursos globais.
            </p>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPermissionsGuideOpen(true)}
              className="gap-2 text-xs"
            >
              <Info className="w-4 h-4 text-primary" />
              Guia de Permissões
            </Button>

            <Button
              size="sm"
              onClick={handleOpenAdd}
              className="gap-2 shadow-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Adicionar Usuário
            </Button>
          </div>
        </div>

        {/* Banner de Sincronização Google Workspace (Gmail & Agenda) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-xl border border-border/70 bg-card shadow-xs">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-500/10 text-red-600 dark:text-red-400">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-xs font-semibold text-foreground">
                  Sincronização Google Workspace (Gmail & Agenda)
                </h4>
                {googleUser ? (
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25 text-[10px] gap-1"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Gmail Conectado ({googleUser.email})
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">
                    Não conectado
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {googleUser
                  ? "Convites de novos membros da equipe são enviados automaticamente pelo seu Gmail. Atividades sincronizam com a Google Agenda."
                  : "Conecte sua conta Google para disparar convites automaticamente pelo seu Gmail para novos usuários e sincronizar com o Google Calendar."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end shrink-0">
            {googleUser ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  disconnectGoogleUser(identity?.id);
                  setGoogleUser(null);
                  notify("Conta Google desconectada.", { type: "info" });
                }}
                className="text-xs text-muted-foreground hover:text-destructive h-8"
              >
                Desconectar
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  connectGoogleOAuth(
                    (u) => {
                      setGoogleUser(u);
                      notify(`Google conectado com sucesso: ${u.email}`, {
                        type: "success",
                      });
                    },
                    (err) => {
                      notify(
                        "Erro ao conectar Google: " +
                          (err?.error || "Cancelado"),
                        {
                          type: "warning",
                        },
                      );
                    },
                    identity?.id,
                  );
                }}
                className="text-xs gap-2 h-8 font-medium border-border"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    fill="#EA4335"
                  />
                </svg>
                Conectar Gmail & Agenda
              </Button>
            )}
          </div>
        </div>

        {/* 5.1 Filtros de status do usuário (Ativo, Convidado, Desativado) com contadores */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center p-1 bg-muted/60 dark:bg-muted/30 rounded-lg border border-border/60">
            <button
              onClick={() => setStatusFilter("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "active"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Ativo
              <Badge
                variant={statusFilter === "active" ? "default" : "secondary"}
                className={`ml-1 text-[11px] h-5 px-1.5 ${
                  statusFilter === "active"
                    ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                    : ""
                }`}
              >
                {counts.active}
              </Badge>
            </button>

            <button
              onClick={() => setStatusFilter("invited")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "invited"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              Convidado
              <Badge
                variant={statusFilter === "invited" ? "default" : "secondary"}
                className={`ml-1 text-[11px] h-5 px-1.5 ${
                  statusFilter === "invited"
                    ? "bg-amber-600 hover:bg-amber-600 text-white"
                    : ""
                }`}
              >
                {counts.invited}
              </Badge>
            </button>

            <button
              onClick={() => setStatusFilter("disabled")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "disabled"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-zinc-400 dark:bg-zinc-600" />
              Desativado
              <Badge
                variant={statusFilter === "disabled" ? "default" : "secondary"}
                className={`ml-1 text-[11px] h-5 px-1.5 ${
                  statusFilter === "disabled"
                    ? "bg-zinc-700 hover:bg-zinc-700 text-white"
                    : ""
                }`}
              >
                {counts.disabled}
              </Badge>
            </button>

            <button
              onClick={() => setStatusFilter("all")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "all"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Todos
              <Badge
                variant={statusFilter === "all" ? "default" : "secondary"}
                className="ml-1 text-[11px] h-5 px-1.5"
              >
                {counts.all}
              </Badge>
            </button>
          </div>

          {/* Ações da lista: campo de busca e botão Exportar */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuário ou e-mail..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-xs bg-background"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportCSV(filteredUsers)}
              className="gap-2 h-9 text-xs font-medium"
            >
              <Download className="w-4 h-4 text-muted-foreground" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Bulk Action Bar (when rows are selected) */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg animate-in fade-in duration-200">
            <div className="flex items-center gap-2 text-xs font-medium text-foreground">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                {selectedIds.length}
              </span>
              usuário(s) selecionado(s)
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => handleBulkStatusChange("active")}
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                Ativar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => handleBulkStatusChange("disabled")}
              >
                <Ban className="w-3.5 h-3.5 text-destructive" />
                Desativar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/20"
                onClick={() => setIsBulkDeleteDialogOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                Excluir
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => {
                  const selUsers = users.filter((u) =>
                    selectedIds.includes(u.id),
                  );
                  handleExportCSV(selUsers);
                }}
              >
                <Download className="w-3.5 h-3.5" />
                Exportar Selecionados
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={() => setSelectedIds([])}
              >
                Limpar seleção
              </Button>
            </div>
          </div>
        )}

        {/* 5.1.1 Tabela de Usuários */}
        <div className="rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 px-4">
                  <Checkbox
                    checked={allFilteredSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Selecionar todos os usuários"
                  />
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[220px]">
                  Nome do usuário
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[200px]">
                  E-mail
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[140px]">
                  Último login
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[130px]">
                  Configuração da conta
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[190px]">
                  Conjunto de permissão — Deals
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground min-w-[190px]">
                  Conjunto de permissão — Global
                </TableHead>
                <TableHead className="font-semibold text-xs text-foreground text-right w-[130px] pr-4">
                  Ações
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-32 text-center text-muted-foreground text-sm"
                  >
                    Carregando usuários do time...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-32 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <Users className="w-8 h-8 text-muted-foreground/40" />
                      <p className="text-sm font-medium">
                        Nenhum usuário encontrado
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Tente ajustar os filtros ou a busca acima.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const you = isCurrentUser(user);
                  const isSelected = selectedIds.includes(user.id);
                  const isUserDisabled =
                    user.status === "disabled" || user.disabled;

                  return (
                    <TableRow
                      key={user.id}
                      className={`transition-colors text-xs ${
                        isSelected ? "bg-primary/5" : ""
                      } ${isUserDisabled ? "opacity-75 bg-muted/20" : ""}`}
                    >
                      {/* Checkbox de seleção */}
                      <TableCell className="px-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(user.id)}
                          aria-label={`Selecionar ${user.first_name}`}
                        />
                      </TableCell>

                      {/* Foto/avatar + Nome do usuário + (você) */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 ring-1 ring-border/50">
                            {user.avatar?.src ? (
                              <AvatarImage
                                src={user.avatar.src}
                                alt={user.first_name}
                              />
                            ) : null}
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">
                              {user.first_name?.[0]}
                              {user.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-foreground">
                              {user.first_name} {user.last_name}
                            </span>
                            {you && (
                              <Badge
                                variant="outline"
                                className="bg-primary/10 text-primary border-primary/20 text-[10px] h-4.5 px-1.5 font-bold"
                              >
                                (você)
                              </Badge>
                            )}
                            {(() => {
                              const userGoogle =
                                getConnectedGoogleUserBySalesId(user.id);
                              if (!userGoogle) return null;
                              return (
                                <Badge
                                  variant="outline"
                                  className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25 text-[10px] h-4.5 px-1.5 font-medium gap-1"
                                  title={`Google conectado: ${userGoogle.email}`}
                                >
                                  <span className="w-1 h-1 rounded-full bg-emerald-500" />
                                  Google ({userGoogle.email.split("@")[0]})
                                </Badge>
                              );
                            })()}
                            {user.dotus_instance_id && (
                              <Badge
                                variant="outline"
                                className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25 text-[10px] h-4.5 px-1.5 font-medium gap-1"
                                title={`Estação WhatsApp Web: ${user.dotus_instance_id}`}
                              >
                                <Phone className="w-2.5 h-2.5 text-emerald-500" />
                                {user.dotus_instance_id}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* E-mail */}
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                          <span
                            className="truncate max-w-[200px]"
                            title={user.email}
                          >
                            {user.email}
                          </span>
                        </div>
                      </TableCell>

                      {/* Último login */}
                      <TableCell className="text-muted-foreground">
                        {user.last_login ? (
                          <div className="flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3 text-muted-foreground/60" />
                            <span>{user.last_login}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground/60 italic text-xs">
                            Nunca acessou
                          </span>
                        )}
                      </TableCell>

                      {/* Configuração da conta (Status da conta: Ativo, Convidado, Desativado) */}
                      <TableCell>
                        {user.status === "active" && !user.disabled && (
                          <Badge
                            variant="outline"
                            className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25 text-[11px] font-medium gap-1"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Ativo
                          </Badge>
                        )}
                        {user.status === "invited" && !user.disabled && (
                          <Badge
                            variant="outline"
                            className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25 text-[11px] font-medium gap-1"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Convidado
                          </Badge>
                        )}
                        {(user.status === "disabled" || user.disabled) && (
                          <Badge
                            variant="outline"
                            className="bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/25 text-[11px] font-medium gap-1"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                            Desativado
                          </Badge>
                        )}
                      </TableCell>

                      {/* 5.2.1 Permissão Deals */}
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex cursor-help">
                              {user.deal_permission === "admin" ? (
                                <Badge
                                  variant="outline"
                                  className="bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30 gap-1 text-[11px] font-medium"
                                >
                                  <Shield className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                  Administrador dos negócios
                                </Badge>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className="gap-1 text-[11px] font-medium text-muted-foreground"
                                >
                                  <UserIcon className="w-3 h-3 text-muted-foreground" />
                                  Usuário comum de negócios
                                </Badge>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs text-xs"
                          >
                            {user.deal_permission === "admin" ? (
                              <div>
                                <p className="font-semibold text-purple-400 mb-0.5">
                                  Administrador dos negócios
                                </p>
                                <p>
                                  Vê e edita todos os dados de vendas (negócios,
                                  leads) e gerencia configurações a nível de
                                  empresa, incluindo motivos de perda e funis.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="font-semibold mb-0.5">
                                  Usuário comum de negócios
                                </p>
                                <p>
                                  Acesso a dados de vendas e ações limitados
                                  conforme o conjunto de permissão
                                  personalizado; padrão para novos usuários.
                                </p>
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>

                      {/* 5.2.2 Permissão Global */}
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex cursor-help">
                              {user.global_permission === "admin" ? (
                                <Badge
                                  variant="outline"
                                  className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 gap-1 text-[11px] font-medium"
                                >
                                  <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                  Administrador global
                                </Badge>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className="gap-1 text-[11px] font-medium text-muted-foreground"
                                >
                                  <UserIcon className="w-3 h-3 text-muted-foreground" />
                                  Usuário comum global
                                </Badge>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs text-xs"
                          >
                            {user.global_permission === "admin" ? (
                              <div>
                                <p className="font-semibold text-blue-400 mb-0.5">
                                  Administrador global
                                </p>
                                <p>
                                  Vê e edita todos os dados de diferentes
                                  produtos (contatos, atividades, relatórios) e
                                  recursos relacionados a acesso.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="font-semibold mb-0.5">
                                  Usuário comum global
                                </p>
                                <p>
                                  Acesso a dados de contatos, atividades e
                                  relatórios limitado conforme conjunto de
                                  permissão personalizado; padrão para novos
                                  usuários.
                                </p>
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>

                      {/* Ações (visão geral, editar, desativar) */}
                      <TableCell className="text-right pr-4">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted"
                                onClick={() => handleOpenOverview(user)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              Visão geral
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted"
                                onClick={() => handleOpenEdit(user)}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              Editar usuário
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={resendingId === user.id}
                                className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                onClick={() => handleResendInvite(user)}
                              >
                                <Send
                                  className={`w-3.5 h-3.5 ${
                                    resendingId === user.id
                                      ? "animate-spin text-primary"
                                      : ""
                                  }`}
                                />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {resendingId === user.id
                                ? "Reenviando..."
                                : "Reenviar convite por e-mail"}
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={you}
                                className={`h-7 w-7 ${
                                  isUserDisabled
                                    ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                                    : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                }`}
                                onClick={() => handleToggleStatus(user)}
                              >
                                {isUserDisabled ? (
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                ) : (
                                  <Ban className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {you
                                ? "Você não pode desativar sua própria conta"
                                : isUserDisabled
                                  ? "Reativar usuário"
                                  : "Desativar usuário"}
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={you || isOwnerUser(user)}
                                className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteUserClick(user)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {you
                                ? "Você não pode excluir sua própria conta"
                                : isOwnerUser(user)
                                  ? "O proprietário do CRM não pode ser excluído"
                                  : "Excluir membro da equipe"}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Modal: Adicionar Usuário */}
        <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Plus className="w-5 h-5 text-primary" />
                Adicionar Usuário ao Time
              </DialogTitle>
              <DialogDescription className="text-xs">
                Convide um novo membro e configure seus conjuntos de permissão
                de Deals e Global.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name" className="text-xs font-medium">
                    Nome *
                  </Label>
                  <Input
                    id="first_name"
                    placeholder="Ex: Ana"
                    value={formData.first_name}
                    onChange={(e) => {
                      const newFirst = e.target.value;
                      setFormData((p) => ({
                        ...p,
                        first_name: newFirst,
                        dotus_instance_id:
                          !p.dotus_instance_id ||
                          p.dotus_instance_id === p.first_name
                            ? newFirst
                            : p.dotus_instance_id,
                      }));
                    }}
                    className="h-8.5 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_name" className="text-xs font-medium">
                    Sobrenome
                  </Label>
                  <Input
                    id="last_name"
                    placeholder="Ex: Martins"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, last_name: e.target.value }))
                    }
                    className="h-8.5 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-medium">
                  E-mail corporativo *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ana.martins@empresa.com.br"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, email: e.target.value }))
                  }
                  className="h-8.5 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="dotus_instance_id"
                  className="text-xs font-medium flex items-center gap-1.5"
                >
                  <Phone className="w-3.5 h-3.5 text-emerald-500" />
                  Estação WhatsApp Web (Dotus WA Call)
                </Label>
                <Input
                  id="dotus_instance_id"
                  placeholder="Ex: comercial-joao"
                  value={formData.dotus_instance_id || ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      dotus_instance_id: e.target.value,
                    }))
                  }
                  className="h-8.5 text-xs font-mono"
                />
                <span className="text-[10px] text-muted-foreground">
                  Identificador da instância configurada no aplicativo do
                  operador para realizar chamadas.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Status da conta</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((p) => ({ ...p, status: "invited" }))
                      }
                      className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                        formData.status === "invited"
                          ? "border-amber-500 bg-amber-500/10 font-semibold text-foreground"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <p className="text-xs">Convidado</p>
                      <p className="text-[10px] text-muted-foreground font-normal">
                        Recebe convite por e-mail
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((p) => ({ ...p, status: "active" }))
                      }
                      className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                        formData.status === "active"
                          ? "border-emerald-500 bg-emerald-500/10 font-semibold text-foreground"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <p className="text-xs">Ativo</p>
                      <p className="text-[10px] text-muted-foreground font-normal">
                        Acesso imediato
                      </p>
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">
                    Convite por e-mail
                  </Label>
                  <label className="flex items-center gap-2 p-2 rounded-lg border border-border h-[52px] cursor-pointer hover:bg-muted/50">
                    <Checkbox
                      checked={formData.send_invite}
                      onCheckedChange={(c) =>
                        setFormData((p) => ({ ...p, send_invite: Boolean(c) }))
                      }
                    />
                    <span className="text-[11px] text-foreground leading-tight">
                      Enviar e-mail para definir senha de acesso
                    </span>
                  </label>
                </div>
              </div>

              {/* 5.2.1 Permissões de negócios (Deals) */}
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-purple-600" />
                  <Label className="text-xs font-semibold text-foreground">
                    Conjunto de permissão — Negócios (Deals)
                  </Label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, deal_permission: "user" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.deal_permission === "user"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-xs text-foreground">
                        Usuário comum de negócios
                      </span>
                      <Badge variant="secondary" className="text-[10px] h-4">
                        Padrão
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Acesso a dados de vendas e ações limitados conforme o
                      conjunto de permissão personalizado.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, deal_permission: "admin" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.deal_permission === "admin"
                        ? "border-purple-600 bg-purple-500/10 ring-1 ring-purple-600"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-xs text-purple-700 dark:text-purple-300 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Administrador dos negócios
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Vê e edita todos os dados de vendas (negócios, leads) e
                      gerencia configurações a nível de empresa (motivos de
                      perda, funis).
                    </p>
                  </button>
                </div>
              </div>

              {/* 5.2.2 Permissões de recursos globais */}
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-600" />
                  <Label className="text-xs font-semibold text-foreground">
                    Conjunto de permissão — Recursos Globais
                  </Label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, global_permission: "user" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.global_permission === "user"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-xs text-foreground">
                        Usuário comum global
                      </span>
                      <Badge variant="secondary" className="text-[10px] h-4">
                        Padrão
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Acesso a contatos, atividades e relatórios limitado
                      conforme conjunto de permissão personalizado.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, global_permission: "admin" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.global_permission === "admin"
                        ? "border-blue-600 bg-blue-500/10 ring-1 ring-blue-600"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Administrador global
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Vê e edita todos os dados de diferentes produtos
                      (contatos, atividades, relatórios) e recursos de acesso.
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddUserOpen(false)}
                className="text-xs"
                disabled={isSavingAdd}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAdd}
                disabled={isSavingAdd}
                className="text-xs gap-1.5 font-medium"
              >
                {isSavingAdd ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Adicionando...
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Adicionar Usuário
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Editar Usuário */}
        <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Pencil className="w-5 h-5 text-primary" />
                Editar Usuário
              </DialogTitle>
              <DialogDescription className="text-xs">
                Atualize informações cadastrais, status da conta e níveis de
                permissão.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="edit_first_name"
                    className="text-xs font-medium"
                  >
                    Nome *
                  </Label>
                  <Input
                    id="edit_first_name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, first_name: e.target.value }))
                    }
                    className="h-8.5 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="edit_last_name"
                    className="text-xs font-medium"
                  >
                    Sobrenome
                  </Label>
                  <Input
                    id="edit_last_name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, last_name: e.target.value }))
                    }
                    className="h-8.5 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit_email" className="text-xs font-medium">
                  E-mail
                </Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, email: e.target.value }))
                  }
                  className="h-8.5 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="edit_dotus_instance_id"
                  className="text-xs font-medium flex items-center gap-1.5"
                >
                  <Phone className="w-3.5 h-3.5 text-emerald-500" />
                  Estação WhatsApp Web (Dotus WA Call)
                </Label>
                <Input
                  id="edit_dotus_instance_id"
                  placeholder="Ex: comercial-joao"
                  value={formData.dotus_instance_id || ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      dotus_instance_id: e.target.value,
                    }))
                  }
                  className="h-8.5 text-xs font-mono"
                />
                <span className="text-[10px] text-muted-foreground">
                  Identificador da instância configurada no aplicativo do
                  operador para realizar chamadas.
                </span>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Status da conta</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, status: "active" }))
                    }
                    className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                      formData.status === "active"
                        ? "border-emerald-500 bg-emerald-500/10 font-semibold text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <p className="text-xs">Ativo</p>
                    <p className="text-[10px] text-muted-foreground font-normal">
                      Conta habilitada
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, status: "invited" }))
                    }
                    className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                      formData.status === "invited"
                        ? "border-amber-500 bg-amber-500/10 font-semibold text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <p className="text-xs">Convidado</p>
                    <p className="text-[10px] text-muted-foreground font-normal">
                      Pendente de ativação
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, status: "disabled" }))
                    }
                    className={`flex-1 p-2 rounded-lg border text-left transition-all ${
                      formData.status === "disabled"
                        ? "border-destructive bg-destructive/10 font-semibold text-destructive"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <p className="text-xs">Desativado</p>
                    <p className="text-[10px] text-muted-foreground font-normal">
                      Acesso bloqueado
                    </p>
                  </button>
                </div>
              </div>

              {/* 5.2.1 Permissão Deals */}
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-purple-600" />
                  <Label className="text-xs font-semibold text-foreground">
                    Conjunto de permissão — Negócios (Deals)
                  </Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, deal_permission: "user" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.deal_permission === "user"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <span className="font-semibold text-xs text-foreground block mb-1">
                      Usuário comum de negócios
                    </span>
                    <p className="text-[11px] text-muted-foreground">
                      Acesso a dados de vendas e ações limitados conforme o
                      conjunto de permissão personalizado.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, deal_permission: "admin" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.deal_permission === "admin"
                        ? "border-purple-600 bg-purple-500/10 ring-1 ring-purple-600"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <span className="font-semibold text-xs text-purple-700 dark:text-purple-300 block mb-1">
                      Administrador dos negócios
                    </span>
                    <p className="text-[11px] text-muted-foreground">
                      Vê e edita todos os dados de vendas (negócios, leads) e
                      gerencia configurações a nível de empresa.
                    </p>
                  </button>
                </div>
              </div>

              {/* 5.2.2 Permissão Global */}
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-600" />
                  <Label className="text-xs font-semibold text-foreground">
                    Conjunto de permissão — Recursos Globais
                  </Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, global_permission: "user" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.global_permission === "user"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <span className="font-semibold text-xs text-foreground block mb-1">
                      Usuário comum global
                    </span>
                    <p className="text-[11px] text-muted-foreground">
                      Acesso a dados de contatos, atividades e relatórios
                      limitado.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({ ...p, global_permission: "admin" }))
                    }
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      formData.global_permission === "admin"
                        ? "border-blue-600 bg-blue-500/10 ring-1 ring-blue-600"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <span className="font-semibold text-xs text-blue-700 dark:text-blue-300 block mb-1">
                      Administrador global
                    </span>
                    <p className="text-[11px] text-muted-foreground">
                      Vê e edita todos os dados de contatos, atividades,
                      relatórios e recursos de acesso.
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 flex-row justify-between items-center">
              <div>
                {activeUser && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={resendingId === activeUser.id}
                    onClick={() => handleResendInvite(activeUser)}
                    className="text-xs gap-1.5 font-medium text-foreground hover:bg-muted"
                  >
                    <Send
                      className={`w-3.5 h-3.5 ${resendingId === activeUser.id ? "animate-spin text-primary" : ""}`}
                    />
                    {resendingId === activeUser.id
                      ? "Reenviando..."
                      : "Reenviar Convite"}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditUserOpen(false)}
                  className="text-xs"
                  disabled={isSavingEdit}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                  className="text-xs font-medium gap-1.5"
                >
                  {isSavingEdit ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    "Salvar Alterações"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Visão Geral do Usuário */}
        <Dialog open={isOverviewOpen} onOpenChange={setIsOverviewOpen}>
          <DialogContent className="max-w-xl">
            {activeUser && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <Eye className="w-5 h-5 text-primary" />
                    Visão Geral do Usuário
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    Detalhes do membro da equipe, status de acesso e regras de
                    permissão.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2 text-xs">
                  {/* Perfil card */}
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/40 border border-border">
                    <Avatar className="h-14 w-14 ring-2 ring-primary/20">
                      {activeUser.avatar?.src && (
                        <AvatarImage
                          src={activeUser.avatar.src}
                          alt={activeUser.first_name}
                        />
                      )}
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                        {activeUser.first_name?.[0]}
                        {activeUser.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-base text-foreground">
                          {activeUser.first_name} {activeUser.last_name}
                        </h3>
                        {isCurrentUser(activeUser) && (
                          <Badge
                            variant="outline"
                            className="bg-primary/10 text-primary text-[10px] h-4 font-bold"
                          >
                            (você)
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground">
                        {activeUser.email}
                      </p>
                      <div className="flex items-center gap-2 pt-1">
                        {activeUser.status === "active" &&
                          !activeUser.disabled && (
                            <Badge
                              variant="outline"
                              className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]"
                            >
                              Conta Ativa
                            </Badge>
                          )}
                        {activeUser.status === "invited" &&
                          !activeUser.disabled && (
                            <Badge
                              variant="outline"
                              className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[10px]"
                            >
                              Convite Pendente
                            </Badge>
                          )}
                        {(activeUser.status === "disabled" ||
                          activeUser.disabled) && (
                          <Badge
                            variant="outline"
                            className="bg-zinc-500/10 text-zinc-600 border-zinc-500/20 text-[10px]"
                          >
                            Conta Desativada
                          </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                          Último login:{" "}
                          {activeUser.last_login || "Nunca acessou"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 5.2.1 Permissão Deals Breakdown */}
                  <div className="p-3.5 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-purple-900 dark:text-purple-200">
                          5.2.1 Permissão de Negócios (Deals)
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          activeUser.deal_permission === "admin"
                            ? "bg-purple-600 text-white border-transparent"
                            : "bg-background text-muted-foreground"
                        }
                      >
                        {activeUser.deal_permission === "admin"
                          ? "Administrador dos negócios"
                          : "Usuário comum de negócios"}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed pl-6">
                      {activeUser.deal_permission === "admin"
                        ? "Vê e edita todos os dados de vendas (negócios, leads) e gerencia configurações a nível de empresa, incluindo motivos de perda e funis."
                        : "Acesso a dados de vendas e ações limitados conforme o conjunto de permissão personalizado; é o padrão para novos usuários."}
                    </p>
                  </div>

                  {/* 5.2.2 Permissão Global Breakdown */}
                  <div className="p-3.5 rounded-xl border border-blue-500/20 bg-blue-500/5 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-blue-900 dark:text-blue-200">
                          5.2.2 Permissão de Recursos Globais
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          activeUser.global_permission === "admin"
                            ? "bg-blue-600 text-white border-transparent"
                            : "bg-background text-muted-foreground"
                        }
                      >
                        {activeUser.global_permission === "admin"
                          ? "Administrador global"
                          : "Usuário comum global"}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed pl-6">
                      {activeUser.global_permission === "admin"
                        ? "Vê e edita todos os dados de diferentes produtos (contatos, atividades, relatórios) e recursos relacionados a acesso."
                        : "Acesso a dados de contatos, atividades e relatórios limitado conforme conjunto de permissão personalizado; é o padrão para novos usuários."}
                    </p>
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0 flex-row justify-between items-center">
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={resendingId === activeUser.id}
                      onClick={() => handleResendInvite(activeUser)}
                      className="text-xs gap-1.5 font-medium text-foreground hover:bg-muted"
                    >
                      <Send
                        className={`w-3.5 h-3.5 ${resendingId === activeUser.id ? "animate-spin text-primary" : ""}`}
                      />
                      {resendingId === activeUser.id
                        ? "Reenviando..."
                        : "Reenviar Convite"}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsOverviewOpen(false);
                        handleOpenEdit(activeUser);
                      }}
                      className="text-xs gap-1.5"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Editar Usuário
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setIsOverviewOpen(false)}
                      className="text-xs"
                    >
                      Fechar
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal: Guia de Conjuntos de Permissão (5.2) */}
        <Dialog
          open={isPermissionsGuideOpen}
          onOpenChange={setIsPermissionsGuideOpen}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="w-5 h-5 text-primary" />
                5.2 Conjuntos de Permissão do CRM Dotus
              </DialogTitle>
              <DialogDescription className="text-xs">
                Entenda os níveis e escopos de acesso concedidos a cada membro
                da equipe.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2 text-xs">
              {/* Seção 5.2.1 */}
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  <h4 className="font-bold text-sm text-foreground">
                    5.2.1 Permissões de Negócios (Deals)
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border border-purple-500/20 bg-purple-500/5 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-purple-700 dark:text-purple-300">
                      <Shield className="w-3.5 h-3.5" />
                      Administrador dos negócios
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Vê e edita todos os dados de vendas (negócios, leads) e
                      gerencia configurações a nível de empresa, incluindo
                      motivos de perda e funis.
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border border-border bg-muted/30 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      Usuário comum de negócios
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Acesso a dados de vendas e ações limitados conforme o
                      conjunto de permissão personalizado; é o padrão para novos
                      usuários.
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 5.2.2 */}
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <h4 className="font-bold text-sm text-foreground">
                    5.2.2 Permissões de Recursos Globais
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-blue-700 dark:text-blue-300">
                      <Globe className="w-3.5 h-3.5" />
                      Administrador global
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Vê e edita todos os dados de diferentes produtos
                      (contatos, atividades, relatórios) e recursos relacionados
                      a acesso.
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border border-border bg-muted/30 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      Usuário comum global
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Acesso a dados de contatos, atividades e relatórios
                      limitado conforme conjunto de permissão personalizado; é o
                      padrão para novos usuários.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                size="sm"
                onClick={() => setIsPermissionsGuideOpen(false)}
                className="text-xs"
              >
                Entendi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmação de Exclusão Individual */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Excluir Membro da Equipe
              </DialogTitle>
              <DialogDescription>
                Esta ação removerá o acesso do usuário ao CRM atual.
              </DialogDescription>
            </DialogHeader>

            {userToDelete && (
              <div className="space-y-3 py-2 text-xs">
                <div className="p-3 bg-muted/40 rounded-lg border space-y-1.5">
                  <div className="font-semibold text-foreground text-sm">
                    {userToDelete.first_name} {userToDelete.last_name}
                  </div>
                  <div className="text-muted-foreground">
                    {userToDelete.email}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 space-y-1">
                  <p className="font-semibold">O que acontece ao excluir?</p>
                  <ul className="list-disc list-inside space-y-0.5 text-[11px] opacity-90">
                    <li>O usuário perderá o acesso imediato a este CRM.</li>
                    <li>
                      O histórico de negócios, notas e tarefas registrados pelo
                      usuário será preservado.
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={isDeleting}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setUserToDelete(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs gap-1.5"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isDeleting ? "Excluindo..." : "Confirmar Exclusão"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmação de Exclusão em Massa */}
        <Dialog
          open={isBulkDeleteDialogOpen}
          onOpenChange={setIsBulkDeleteDialogOpen}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Excluir Usuários Selecionados
              </DialogTitle>
              <DialogDescription>
                Tem certeza de que deseja remover {selectedIds.length}{" "}
                usuário(s) selecionado(s) da equipe deste CRM?
              </DialogDescription>
            </DialogHeader>

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 text-xs space-y-1">
              <p className="font-semibold">Atenção:</p>
              <p className="text-[11px] opacity-90">
                Os usuários selecionados perderão o acesso a este CRM
                imediatamente.
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={isBulkDeleting}
                onClick={() => setIsBulkDeleteDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs gap-1.5"
                disabled={isBulkDeleting}
                onClick={handleConfirmBulkDelete}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isBulkDeleting
                  ? "Excluindo..."
                  : `Excluir (${selectedIds.length})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
